// DM-1/DM-2/DM-5: document construction and invariant validation.
import type { Doc, DimStyle, Entity, Layer, Style, TextStyle } from "./entities";

export function newId(): string {
  return crypto.randomUUID();
}

export function defaultStyle(): Style {
  return {
    strokeColor: "#e8eaf0",
    strokeWidth: 0.35,
    widthMode: "world",
    dash: "solid",
    fill: { mode: "none", color: "#4dd0e1", opacity: 0.35 },
    opacity: 1
  };
}

export function defaultTextStyle(): TextStyle {
  return { font: "Inter", size: 3.5, color: "#e8eaf0", opacity: 1, align: "left", bold: false };
}

export function defaultDimStyle(): DimStyle {
  return {
    textStyle: { ...defaultTextStyle(), size: 3 },
    arrowMm: 2.5,
    extOvershootMm: 2,
    extGapMm: 1,
    widthMode: "screen",
    strokeWidthPx: 1,
    color: "#8ab4f8"
  };
}

/** DM-2: new documents ship exactly Background / Drawing / Annotations. */
export function createEmptyDoc(name: string): Doc {
  const now = new Date().toISOString();
  const layers: Layer[] = [
    { id: newId(), name: "Background", color: "#7a8194", visible: true, locked: false, opacity: 1, print: true },
    { id: newId(), name: "Drawing", color: "#e8eaf0", visible: true, locked: false, opacity: 1, print: true },
    { id: newId(), name: "Annotations", color: "#8ab4f8", visible: true, locked: false, opacity: 1, print: true }
  ];
  const zOrder: Record<string, string[]> = {};
  for (const l of layers) zOrder[l.id] = [];
  return {
    schema: 1,
    id: newId(),
    name,
    createdAt: now,
    modifiedAt: now,
    units: "mm",
    precision: 1,
    defaultStyle: defaultStyle(),
    dimStyle: defaultDimStyle(),
    canvasGrid: { visible: true, snap: false },
    layers,
    entities: {},
    zOrder,
    assets: {}
  };
}

/** The Drawing layer of a fresh doc (index 1 by construction). */
export function defaultActiveLayerId(doc: Doc): string {
  return (doc.layers[1] ?? doc.layers[0])!.id;
}

function walkNumbers(value: unknown, path: string, errors: string[]): void {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) errors.push(`INV-10: non-finite number at ${path}`);
  } else if (value === null) {
    // No field in the schema is null; JSON.stringify(NaN) === "null", so this also catches INV-10 via JSON.
    errors.push(`INV-10: null at ${path}`);
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => walkNumbers(v, `${path}[${i}]`, errors));
  } else if (value !== null && typeof value === "object") {
    for (const [k, v] of Object.entries(value)) walkNumbers(v, `${path}.${k}`, errors);
  }
}

/** DM-5 invariants. Returns a list of violations (empty = valid). */
export function validateDoc(doc: Doc): string[] {
  const errors: string[] = [];
  const layerIds = new Set(doc.layers.map((l) => l.id));
  const groupChildren = new Set<string>();

  for (const entity of Object.values(doc.entities)) {
    if (entity.type === "group") {
      for (const childId of entity.children) {
        if (!doc.entities[childId]) errors.push(`INV-2: group ${entity.id} child ${childId} missing`);
        if (childId === entity.id) errors.push(`INV-2: group ${entity.id} contains itself`);
        groupChildren.add(childId);
      }
    }
  }

  const seenInZ = new Set<string>();
  for (const [layerId, ids] of Object.entries(doc.zOrder)) {
    if (!layerIds.has(layerId)) errors.push(`INV-1: zOrder references unknown layer ${layerId}`);
    for (const id of ids) {
      if (!doc.entities[id]) errors.push(`INV-1: zOrder references unknown entity ${id}`);
      if (seenInZ.has(id)) errors.push(`INV-1: entity ${id} appears in zOrder twice`);
      seenInZ.add(id);
      if (groupChildren.has(id)) errors.push(`INV-1: group child ${id} must not appear in zOrder`);
    }
  }

  for (const entity of Object.values(doc.entities)) {
    if (!layerIds.has(entity.layerId)) errors.push(`INV-1: entity ${entity.id} has unknown layer`);
    if (!groupChildren.has(entity.id) && !seenInZ.has(entity.id)) {
      errors.push(`INV-1: entity ${entity.id} missing from zOrder`);
    }
    switch (entity.type) {
      case "polyline":
        if (entity.points.length < (entity.closed ? 3 : 2)) {
          errors.push(`INV-4: polyline ${entity.id} has too few points`);
        }
        break;
      case "circle":
        if (!(entity.r > 0)) errors.push(`INV-5: circle ${entity.id} r <= 0`);
        break;
      case "arc":
        if (!(entity.r > 0)) errors.push(`INV-5: arc ${entity.id} r <= 0`);
        if (!(Math.abs(entity.sweepDeg) > 0) || Math.abs(entity.sweepDeg) > 360) {
          errors.push(`INV-5: arc ${entity.id} invalid sweep`);
        }
        break;
      case "text":
      case "label":
        if (entity.content.trim().length === 0) errors.push(`INV-9: ${entity.type} ${entity.id} empty`);
        break;
      case "image":
        if (!doc.assets[entity.assetHash]) errors.push(`INV-7: image ${entity.id} asset missing`);
        if (!(entity.wMm > 0) || !(entity.hMm > 0)) errors.push(`INV-7: image ${entity.id} bad size`);
        break;
      case "grid":
        if (entity.cols < 1 || entity.cols > 1000 || entity.rows < 1 || entity.rows > 1000) {
          errors.push(`INV-8: grid ${entity.id} cols/rows out of range`);
        }
        break;
      case "dimension":
        for (const anchor of [entity.a, entity.b]) {
          if (anchor.kind === "entity" && !doc.entities[anchor.entityId]) {
            errors.push(`INV-6: dimension ${entity.id} anchors deleted entity`);
          }
        }
        break;
      case "marker":
      case "group":
        break;
    }
  }

  walkNumbers(doc.entities, "entities", errors);
  return errors;
}

/** INV-2 effective visibility/locked helpers (group-aware resolution is added with groups in M2). */
export function layerOf(doc: Doc, entity: Entity): Layer | undefined {
  return doc.layers.find((l) => l.id === entity.layerId);
}
